import type {
  DropdownComponentActionConfig,
  Ref,
  SummaryConfig,
} from '@repo/sdk/models/components';

import { MANUAL_MODE_PREFIX, MODE_DROPDOWN_STATE_PREFIX } from './manual-devices.constants';
import type { ManualDevice } from './manual-devices.types';

/** The device's mode dropdown config, when it has one with a value ref. */
export function findModeDropdown(
  summary: SummaryConfig
): (DropdownComponentActionConfig & { valueRef: Ref }) | undefined {
  for (const actionComponent of summary.actionComponents ?? []) {
    for (const state of actionComponent.states ?? []) {
      if (!state.stateName?.startsWith(MODE_DROPDOWN_STATE_PREFIX)) continue;
      const config = state.actionComponentConfig?.dropdownComponentActionConfig;
      if (config?.valueRef) return { ...config, valueRef: config.valueRef };
    }
  }
  return undefined;
}

/** Normalizes a live mode setting value to the string form used by dropdown options. */
export function toModeValue(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return String(value);
}

/** Display label of a live mode value: the matching option's label, else the raw value. */
export function getModeLabel(
  config: DropdownComponentActionConfig,
  value: unknown
): string | undefined {
  const modeValue = toModeValue(value);
  if (modeValue === undefined) return undefined;
  return config.options?.find((option) => option.value === modeValue)?.label ?? modeValue;
}

/** True when the mode label starts with "Manual", ignoring case. */
export function isManualModeLabel(label: string | undefined): boolean {
  return label?.toLowerCase().startsWith(MANUAL_MODE_PREFIX.toLowerCase()) ?? false;
}

/** True when both lists hold the same devices, in order, with the same mode labels. */
export function areManualDeviceListsEqual(a: ManualDevice[], b: ManualDevice[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (device, index) =>
        device.referenceId === b[index].referenceId && device.modeLabel === b[index].modeLabel
    )
  );
}
